
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, RefreshCw } from "lucide-react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { triggerScheduledUpdates } from "@/utils/engagementMetrics";

const scheduleSchema = z.object({
  enabled: z.boolean(),
  delay_seconds: z.number().min(1).max(60),
  interval_type: z.enum(["hourly", "daily", "weekly", "custom"]),
  custom_time: z.string().nullable().optional(),
  custom_day: z.string().nullable().optional(),
});

type ScheduleFormValues = z.infer<typeof scheduleSchema>;

interface IntegrationScheduleData {
  id: string;
  integration_type: string;
  enabled: boolean;
  delay_seconds: number;
  interval_type: string;
  custom_time: string | null;
  custom_day: string | null;
}

interface IntegrationSchedulesProps {
  integrationType: "youtube" | "twitter" | "telegram";
  title: string;
  description: string;
}

export function IntegrationSchedules({ integrationType, title, description }: IntegrationSchedulesProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [scheduleData, setScheduleData] = useState<IntegrationScheduleData | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const { toast } = useToast();

  const defaultValues: ScheduleFormValues = {
    enabled: false,
    delay_seconds: 5,
    interval_type: "daily",
    custom_time: null,
    custom_day: null,
  };

  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm<ScheduleFormValues>({
    resolver: zodResolver(scheduleSchema),
    defaultValues
  });

  // Watch the interval type to determine if custom fields should be shown
  const intervalType = watch("interval_type");
  const enabled = watch("enabled");

  useEffect(() => {
    // Fetch existing schedule settings
    const fetchSchedule = async () => {
      try {
        setLoading(true);
        
        const { data, error } = await supabase
          .from('integration_schedules')
          .select('*')
          .eq('integration_type', integrationType)
          .maybeSingle();
          
        if (error) {
          throw error;
        }
        
        if (data) {
          setScheduleData(data);
          setValue("enabled", data.enabled || false);
          setValue("delay_seconds", data.delay_seconds || 5);
          setValue("interval_type", (data.interval_type as any) || "daily");
          setValue("custom_time", data.custom_time || null);
          setValue("custom_day", data.custom_day || null);
        }
        
        // Fetch the last updated post for this platform
        const { data: postData, error: postError } = await supabase
          .from('kol_tracker')
          .select('updated_at')
          .eq('platform', integrationType)
          .eq('integration_enable', true)
          .order('updated_at', { ascending: false })
          .limit(1)
          .maybeSingle();
          
        if (!postError && postData?.updated_at) {
          setLastUpdated(new Date(postData.updated_at).toLocaleString());
        }
      } catch (error) {
        console.error(`Error fetching ${integrationType} schedule:`, error);
        toast({
          title: "Error Loading Schedule",
          description: `Failed to load ${integrationType} update schedule.`,
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchSchedule();
  }, [integrationType, setValue, toast]);

  const updateKolTrackerIntegration = async (enabled: boolean) => {
    try {
      // Update all kol_tracker entries to match the schedule's enabled status
      const { error } = await supabase
        .from('kol_tracker')
        .update({ integration_enable: enabled })
        .eq('platform', integrationType);
        
      if (error) throw error;
      
      console.log(`Updated all ${integrationType} kol_tracker integration_enable to ${enabled}`);
    } catch (error) {
      console.error(`Error updating ${integrationType} integration status:`, error);
    }
  };

  const onSubmit = async (formData: ScheduleFormValues) => {
    try {
      setSaving(true);
      
      // Prepare data for insert/update
      const scheduleUpdateData = {
        integration_type: integrationType,
        enabled: formData.enabled,
        delay_seconds: formData.delay_seconds,
        interval_type: formData.interval_type,
        custom_time: formData.interval_type === "custom" ? formData.custom_time : null,
        custom_day: formData.interval_type === "weekly" ? formData.custom_day : null
      };
      
      // If we have existing data, update it, otherwise insert new
      if (scheduleData?.id) {
        const { error } = await supabase
          .from('integration_schedules')
          .update(scheduleUpdateData)
          .eq('id', scheduleData.id);
          
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('integration_schedules')
          .insert(scheduleUpdateData);
          
        if (error) throw error;
      }
      
      // Update all KOL Tracker entries platform integration_enable status to match this setting
      await updateKolTrackerIntegration(formData.enabled);
      
      toast({
        title: "Schedule Updated",
        description: `${integrationType} update schedule has been saved.`,
      });
      
      // Refetch after save to update local state
      const { data, error } = await supabase
        .from('integration_schedules')
        .select('*')
        .eq('integration_type', integrationType)
        .maybeSingle();
      
      if (error) throw error;
      if (data) setScheduleData(data);
      
    } catch (error) {
      console.error(`Error saving ${integrationType} schedule:`, error);
      toast({
        title: "Error Saving Schedule",
        description: `Failed to save ${integrationType} update schedule.`,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const runUpdatesNow = async () => {
    try {
      setRunning(true);
      toast({
        title: "Running Updates",
        description: `Manually running ${integrationType} updates now...`,
      });
      
      const result = await triggerScheduledUpdates(integrationType);
      
      toast({
        title: "Updates Complete",
        description: result.message,
      });
      
      // Update the last updated time
      const { data: postData, error: postError } = await supabase
        .from('kol_tracker')
        .select('updated_at')
        .eq('platform', integrationType)
        .eq('integration_enable', true)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
        
      if (!postError && postData?.updated_at) {
        setLastUpdated(new Date(postData.updated_at).toLocaleString());
      }
    } catch (error: any) {
      console.error(`Error running ${integrationType} updates:`, error);
      toast({
        title: "Update Error",
        description: error.message || `Failed to run ${integrationType} updates.`,
        variant: "destructive",
      });
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">{title.toUpperCase()} UPDATE SCHEDULE</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent className="flex justify-center py-6">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-sm">
      <CardHeader>
        <CardTitle className="text-lg">{title.toUpperCase()} UPDATE SCHEDULE</CardTitle>
        <CardDescription>{description}</CardDescription>
        {lastUpdated && (
          <div className="text-xs text-muted-foreground mt-1">
            Last updated: {lastUpdated}
          </div>
        )}
      </CardHeader>
      <CardContent>
        <form id={`${integrationType}-schedule-form`} onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="flex items-center space-x-2">
            <Switch 
              id={`${integrationType}-enabled`}
              checked={enabled}
              onCheckedChange={(checked) => setValue("enabled", checked)}
            />
            <Label htmlFor={`${integrationType}-enabled`}>
              Enable automatic updates
            </Label>
          </div>
          
          <div className="grid gap-4">
            <div>
              <Label htmlFor={`${integrationType}-delay`}>Delay between requests (seconds)</Label>
              <Input
                id={`${integrationType}-delay`}
                type="number"
                className="bg-white dark:bg-transparent/10 border-gray-300 dark:border-[#4A4A4A]"
                min={1}
                max={60}
                {...register("delay_seconds", { valueAsNumber: true })}
                disabled={!enabled}
              />
              {errors.delay_seconds && (
                <p className="text-sm text-red-500 mt-1">
                  Delay must be between 1 and 60 seconds
                </p>
              )}
            </div>
            
            <div>
              <Label htmlFor={`${integrationType}-interval`}>Update frequency</Label>
              <Select 
                value={intervalType} 
                onValueChange={(value) => setValue("interval_type", value as any)}
                disabled={!enabled}
              >
                <SelectTrigger id={`${integrationType}-interval`} className="bg-white dark:bg-transparent/10 border-gray-300 dark:border-[#4A4A4A]">
                  <SelectValue placeholder="Select interval" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hourly">Hourly</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {intervalType === "weekly" && (
              <div>
                <Label htmlFor={`${integrationType}-day`}>Day of the week</Label>
                <Select 
                  value={watch("custom_day") || "monday"} 
                  onValueChange={(value) => setValue("custom_day", value)}
                  disabled={!enabled}
                >
                  <SelectTrigger id={`${integrationType}-day`} className="bg-white dark:bg-transparent/10 border-gray-300 dark:border-[#4A4A4A]">
                    <SelectValue placeholder="Select day" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monday">Monday</SelectItem>
                    <SelectItem value="tuesday">Tuesday</SelectItem>
                    <SelectItem value="wednesday">Wednesday</SelectItem>
                    <SelectItem value="thursday">Thursday</SelectItem>
                    <SelectItem value="friday">Friday</SelectItem>
                    <SelectItem value="saturday">Saturday</SelectItem>
                    <SelectItem value="sunday">Sunday</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {(intervalType === "custom" || intervalType === "daily" || intervalType === "weekly") && (
              <div>
                <Label htmlFor={`${integrationType}-time`}>Time</Label>
                <Input
                  id={`${integrationType}-time`}
                  type="time"
                  className="bg-white dark:bg-transparent/10 border-gray-300 dark:border-[#4A4A4A]"
                  {...register("custom_time")}
                  disabled={!enabled}
                />
              </div>
            )}
          </div>
        </form>
      </CardContent>
      <CardFooter className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-2 w-full">
        <Button 
          type="submit"
          form={`${integrationType}-schedule-form`}
          variant="default"
          className="bg-blue-600 hover:bg-blue-700 text-white dark:bg-dashboard-accent dark:hover:bg-opacity-80"
          disabled={saving || !enabled}
        >
          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Save Schedule
        </Button>
        
        <Button 
          variant="outline"
          className="flex-1 sm:flex-none"
          onClick={runUpdatesNow}
          disabled={running || !enabled}
        >
          {running ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          Run Updates Now
        </Button>
      </CardFooter>
    </Card>
  );
}
